import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useRouter } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { errorMessage } from '@/lib/api'
import { applyServerErrors } from '@/lib/form-errors'
import { authKeys, meQueryOptions, useRegister } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { safeRedirect } from '../lib'

const route = getRouteApi('/register')

export const registerSchema = z
  .object({
    name: z.string().trim().min(2, 'Enter your full name').max(120),
    email: z.email('Enter a valid email address'),
    password: z.string().min(8, 'Use at least 8 characters'),
    password_confirmation: z.string(),
  })
  .refine((v) => v.password === v.password_confirmation, { path: ['password_confirmation'], message: 'Passwords don’t match' })
type Values = z.infer<typeof registerSchema>

function passwordStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++
  return Math.min(score, 4) as 0 | 1 | 2 | 3 | 4
}

const STRENGTH = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const
const STRENGTH_COLOR = ['bg-muted', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'] as const

export function RegisterPage() {
  const { redirect, email } = route.useSearch()
  const register = useRegister()
  const qc = useQueryClient()
  const router = useRouter()
  const form = useForm<Values>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: email ?? '', password: '', password_confirmation: '' },
  })
  const { errors, isSubmitting } = form.formState
  const pw = form.watch('password')
  const strength = passwordStrength(pw)

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await register.mutateAsync(values)
      await qc.invalidateQueries({ queryKey: authKeys.me() })
      await qc.fetchQuery(meQueryOptions)
      router.history.push(safeRedirect(redirect, '/onboarding'))
    } catch (error) {
      if (!applyServerErrors(error, form.setError, { fields: ['name', 'email', 'password', 'password_confirmation'] })) {
        form.setError('root.server', { message: errorMessage(error) })
      }
    }
  })

  return (
    <AuthLayout
      title="Create your account"
      description="Start your free trial — no credit card required."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" search={{ redirect }} className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError message={errors.root?.server?.message} />
        <Field label="Full name" error={errors.name?.message}>
          <Input autoComplete="name" placeholder="Jordan Lee" autoFocus {...form.register('name')} />
        </Field>
        <Field label="Work email" error={errors.email?.message}>
          <Input type="email" autoComplete="email" placeholder="you@company.com" readOnly={!!email} {...form.register('email')} />
        </Field>
        <Field label="Password" error={errors.password?.message}>
          <Input type="password" autoComplete="new-password" placeholder="At least 8 characters" {...form.register('password')} />
        </Field>
        {pw && (
          <div className="-mt-2 flex items-center gap-2" aria-live="polite">
            <div className="flex flex-1 gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength ? STRENGTH_COLOR[strength] : 'bg-muted'}`} />
              ))}
            </div>
            <span className="w-16 text-right text-xs text-muted-foreground">{STRENGTH[strength]}</span>
          </div>
        )}
        <Field label="Confirm password" error={errors.password_confirmation?.message}>
          <Input type="password" autoComplete="new-password" {...form.register('password_confirmation')} />
        </Field>
        <Button type="submit" size="lg" variant="brand" loading={isSubmitting} className="mt-2 w-full">
          Create account
        </Button>
        <p className="text-center text-xs text-muted-foreground">By creating an account you agree to the Terms of Service and Privacy Policy.</p>
      </form>
    </AuthLayout>
  )
}
